<template>
  <div class="col-12 pt3">
    <div class="mt2 red center bold h6">
      {{ errorMessage }}
    </div>

    <div v-if="unpaidBookings.length" class="col-12 mt3">
      <div
        v-for="booking in unpaidBookings"
        :key="booking.key"
        class="col-12 white-bg card-top border p2 mb2"
      >
        <div class="flex flex-wrap items-center">
          <div class="col-8 sm-col-12 xs-col-12 pr2">
            <h5 class="m0 bold break-word">{{ booking.sessionName || 'Réservation en impayé' }}</h5>
            <span v-if="booking.orderUuid" class="h6 dark-grey block mt1 break-word">
              Commande {{ booking.orderUuid }}
            </span>
          </div>

          <div class="col-4 sm-col-12 xs-col-12">
            <Button
              :action="() => handlePayment(booking)"
              :disabled="!booking.redirectUrl"
              color="violet-bg"
              border="violet-bd"
              text-size="h6"
              text="Régulariser"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="unpaidSubscriptions.length" class="col-12 mt3">
      <div
        v-for="subscription in unpaidSubscriptions"
        :key="subscription.key"
        class="col-12 white-bg card-top border mb2"
      >
        <button
          type="button"
          class="unpaid-subscription-toggle col-12 flex flex-wrap items-center p2 pointer left-align border-none white-bg"
          :aria-expanded="isSubscriptionOpen(subscription.key)"
          :aria-controls="`unpaid-invoices-${subscription.key}`"
          @click="toggleSubscription(subscription.key)"
        >
          <div class="flex-auto pr2">
            <h5 class="m0 bold break-word">{{ subscription.name || 'Abonnement en impayé' }}</h5>
            <span class="h6 dark-grey block mt1">{{ invoicesSummary(subscription) }}</span>
          </div>
          <i :class="['icon', isSubscriptionOpen(subscription.key) ? 'icon-angle-up-icon' : 'icon-angle-down-icon']"></i>
        </button>

        <div
          v-if="isSubscriptionOpen(subscription.key)"
          :id="`unpaid-invoices-${subscription.key}`"
          class="col-12 px2 pb2"
        >
          <div
            v-for="invoice in subscription.invoices"
            :key="invoice.key"
            class="col-12 flex flex-wrap items-center border-top py2"
          >
            <div class="col-8 sm-col-12 xs-col-12 pr2">
              <span class="h6 bold block">Facture {{ invoice.reference }}</span>
            </div>

            <div class="col-4 sm-col-12 xs-col-12">
              <Button
                :action="() => handlePayment(invoice)"
                color="violet-bg"
                border="violet-bd"
                text-size="h6"
                text="Régulariser ton abonnement"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import { computed, ref } from 'vue';
import Cookies from 'js-cookie';
import { useStore } from '@/store/index';

import Button from '@/components/elements/Button.vue';

/* Read by Confirmation.vue once the Sharegroop tab redirects back, to route the customer into the court flow instead of the generic confirmation page. */
const UNPAID_REDIRECT_COOKIE = 'unpaid_redirect_target';
const UNPAID_REDIRECT_EXPIRATION_MINUTES = 20;

const store = useStore();

const openSubscriptionKeys = ref([]);

const errorMessage = computed(() => store.state.courtSettings?.errorMessage);

const unpaidBookings = computed(() => store.state.courtSettings?.unpaidBookings || []);

const unpaidSubscriptions = computed(() => store.state.courtSettings?.unpaidSubscriptions || []);

const isSubscriptionOpen = key => openSubscriptionKeys.value.includes(key);

const toggleSubscription = key => {
  openSubscriptionKeys.value = isSubscriptionOpen(key)
    ? openSubscriptionKeys.value.filter(openKey => openKey !== key)
    : [...openSubscriptionKeys.value, key];
};

const invoicesSummary = subscription => {
  const { length } = subscription.invoices;

  return `${length} facture${length > 1 ? 's' : ''} à régulariser`;
};

/* `noopener` prevents the Sharegroop page from reaching back into this tab via window.opener. */
const handlePayment = item => {
  if (!item.redirectUrl) return;

  /* Sharegroop's success redirect lands in the tab it opens, not this one, so this cookie is
   * how Confirmation.vue (loaded in that tab) knows to route the customer back into the court
   * flow instead of showing the generic confirmation screen. */
  Cookies.set(UNPAID_REDIRECT_COOKIE, 'court', { expires: UNPAID_REDIRECT_EXPIRATION_MINUTES / (24 * 60) });

  window.open(item.redirectUrl, '_blank', 'noopener');
};

</script>

<style scoped>
  /* Buttons carry a UA font shorthand that shrinks the em-based heading inside them. */
  .unpaid-subscription-toggle {
    font: inherit;
    color: inherit;
  }
</style>
